import { FormProvider } from "react-hook-form";
import { Message } from "primereact/message";
import { Sidebar } from "primereact/sidebar";
import { useNavigate, useParams } from "react-router";

import { cn } from "@/styles/cn";
import { Button, CenterLayout } from "@/components";
import { CircleAlertIcon } from "@/components/atoms/icons";
import { SelectMultipleinline } from "@/components/molecules/SelectMultipleinline";
import { OptionValue } from "@/components/types/option";
import { useGetAllMaterials } from "@/config/services";
import { PathNamekAppsRoutes, PathNamekRoutes } from "@/router";
import type { MaterialItem } from "@/service/namek/useGetMaterialsByCVId";

import { AssignMaterialsInfo } from "../AssignMaterialsInfo";

import { useAssignMaterialForm } from "./AssignMaterialForm.hook";

export function AssignMaterialEditor() {
  const { data: materials = [] } = useGetAllMaterials();

  const { edition } = useParams();
  const navigate = useNavigate();

  const id = isNaN(Number(edition)) ? 0 : Number(edition);

  const closeForm = () => {
    navigate(`/${PathNamekAppsRoutes.CONFIG}/${PathNamekRoutes.CONFIG.ASSIGN_MATERIALS}`, {
      replace: true,
    });
  };

  const { form, onSubmit, isPending, assignMaterial, isFetching } = useAssignMaterialForm({
    id,
    onDone: closeForm,
  });

  const selected = form.watch("materials");
  const isSubmitted = form.formState.isSubmitted;

  const handleSelectChange = (value: OptionValue<MaterialItem>[]) => {
    form.setValue("materials", value as MaterialItem[], {
      shouldDirty: true,
      shouldValidate: isSubmitted,
    });
  };

  return (
    <Sidebar
      className="w-full bg-white py-[1.5rem] min-[420px]:w-[420px]"
      dismissable={false}
      header="Materiales asignados"
      position="right"
      pt={{
        header: {
          className: cn("text-urbe-primary text-lg font-bold mb-8 px-[1.375rem]"),
        },
        mask: {
          className: cn(
            "flex pointer-events-auto",
            "bg-black bg-opacity-40 transition duration-200 z-20 transition-colors",
          ),
        },
      }}
      visible={true}
      onHide={closeForm}
    >
      {isFetching && <CenterLayout>Cargando...</CenterLayout>}
      {!isFetching && (
        <FormProvider {...form}>
          <form className="flex h-full flex-col gap-5" onSubmit={form.handleSubmit(onSubmit)}>
            <div className="flex h-full flex-1 flex-col gap-5 overflow-auto px-[1.375rem]">
              {assignMaterial && (
                <AssignMaterialsInfo
                  cooperative={assignMaterial.cooperative.name}
                  greenCenter={assignMaterial.name}
                />
              )}

              <div className="min-h-[300px]">
                <SelectMultipleinline<MaterialItem>
                  filter
                  showSelecteAll
                  className="flex h-[45dvh] min-h-[260px] flex-col min-[480px]:h-[55dvh] md:h-[60dvh]"
                  dataKey="id"
                  listClassName="min-h-[200px]"
                  optionLabel="name"
                  options={materials}
                  placeholder="Buscar materiales"
                  value={selected}
                  onChange={handleSelectChange}
                />
              </div>
            </div>
            {isSubmitted && form.formState.errors.materials?.message && (
              <div className="px-[1.375rem]">
                <Message
                  icon={<CircleAlertIcon className="text-error-1000" size={16} />}
                  pt={{
                    root: {
                      className:
                        "items-center border flex gap-2 px-2 bg-error-200 border-error-400 justify-start gap-2",
                    },
                    text: {
                      className: "text-error-1000",
                    },
                  }}
                  severity="error"
                  text={form.formState.errors.materials?.message}
                />
              </div>
            )}
            <div className="flex flex-row justify-end gap-4 px-[1.375rem]">
              <Button disabled={isPending} severity="secondary" type="button" onClick={closeForm}>
                Cancelar
              </Button>
              <Button className="px-5" loading={isPending} type="submit">
                Guardar
              </Button>
            </div>
          </form>
        </FormProvider>
      )}
    </Sidebar>
  );
}
